<template>
  <div class="main">
    <div class="header">
      <h3>
        <a-tooltip placement="rightTop">
          <template slot="title">
            There are {{ maxConcurrentSessions }} session slots at your
            disposal. {{ sessions.length }} of these are currently occupied by
            you. These sessions are displayed in the list below. If you have not
            yet used up all the session slots, you can create another session by
            clicking the 'Add new' button. To delete a session, you can use the
            trash can icon in the corresponding session slot.
          </template>
          Registered sessions ({{ sessions.length }} /
          {{ maxConcurrentSessions }})
        </a-tooltip>
      </h3>
      <Button
        class="create-new-session-button"
        @onClick="$emit('addSession')"
        :disabled="maxConcurrentSessions === sessions.length"
      >
        Add new
        <font-awesome-icon
          icon="plus-circle"
          data-test="add-icon"
          role="button"
          class="add-icon"
          :class="
            maxConcurrentSessions === sessions.length ? 'transparent' : ''
          "
        />
      </Button>
    </div>
    <div class="content">
      <section v-if="!isFetchingSession" class="activeSesstionContainer">
        <div>
          <div
            v-for="session of sessions"
            v-bind:key="session.id"
            class="activeSession"
            :class="{
              opacity: !isSessionStateStable(session)
            }"
          >
            <a-collapse
              :accordion="true"
              v-model="activeKey"
              :bordered="false"
              class="accordionHeader"
              :class="{
                active: isMachineActive(session)
              }"
            >
              <a-collapse-panel
                class="accordionPanel"
                :key="session.id"
                :header="getSessionName(session)"
              >
                <SessionDetails
                  v-if="isSessionStateStable(session)"
                  @vmStartingOrStopping="handleVMStartingOrStopping"
                  @refetchSessions="handleSessionRefresh"
                  @newWindowClick="handleWindowClick"
                  @restartSession="handleRestartSession"
                  @stopSession="handleStopSession"
                  @downloadRDP="handleRDPFileDownload"
                  :newWindowLabel="newWindowLabel"
                  :session="session"
                  :isVNCOpening="isVNCOpening"
                />
                <section v-else>
                  <a-spin
                    tip="Updating session state..."
                    size="large"
                    class="spinner"
                  />
                </section>
                <a-icon
                  class="icon"
                  v-if="machineState(session) === 'RUNNING'"
                  slot="extra"
                  type="desktop"
                  @click.stop="handleRDPFileDownload(session.id)"
                />
                <a-icon
                  class="icon"
                  v-if="machineState(session) === 'RUNNING'"
                  slot="extra"
                  type="pause-circle"
                  @click.stop="handleStopSession(session.id)"
                />
                <a-icon
                  v-else-if="machineState(session) === 'STOPPED'"
                  slot="extra"
                  type="play-circle"
                  @click.stop="
                    handleRestartSession({
                      sessionId: session.id,
                      timeoutHours: session.timeoutHours,
                      useVPNSetup: session.useVPNSetup
                    })
                  "
                />
                <a-icon
                  v-if="isSessionStateStable(session)"
                  slot="extra"
                  type="delete"
                  data-test="delete-session-button"
                  @click="e => handleClick(e, session.id)"
                />
              </a-collapse-panel>
            </a-collapse>
          </div>
        </div>
        <div class="actions">
          <span
            >Currently running {{ activeSessionCount }}/{{
              sessions.length
            }}</span
          >
          <Button
            :disabled="
              activeSessionCount === 0 || processing || vmStartingOrStopping
            "
            @onClick="stopAllVMSessions"
            class="button"
            >Stop all</Button
          >
        </div>
      </section>

      <section v-else class="activeSesstionContainer">
        <a-spin tip="Updating session state..." size="large" class="spinner" />
      </section>
      <section class="creditContainer">
        <div>
          <h3>Credits</h3>
          <section v-if="!isFetchingSession">
            <div>
              <a-tooltip placement="rightTop">
                <template slot="title">
                  Displays the remaining credits in relation to the credits
                  available each month ({{ totalCreditsForThisMonth }} credits /
                  month). At the beginning of each new month, these credits are
                  automatically replenished.
                </template>
                <strong>Remaining credits</strong>
                <a-progress
                  type="dashboard"
                  :percent="remainingCreditsPercentage"
                  :stroke-color="'#1890ff'"
                  :format="() => remainingCreditsString"
                  status="normal"
                  data-test="remaining-credits"
                />
              </a-tooltip>
            </div>
            <div>
              <a-tooltip placement="rightTop">
                <template slot="title">
                  An approximate prediction of how many days or hours the active
                  configuration of sessions can be operated. This is in relation
                  to the remaining days until the credits are replenished.
                </template>
                <strong>Forecast credits</strong>
                <a-progress
                  type="dashboard"
                  :percent="getRemainingDaysInMonth.percentage"
                  :stroke-color="'#1890ff'"
                  status="normal"
                  :format="() => getRemainingDaysInMonth.formatString"
                  data-test="forecast-credits"
                />
              </a-tooltip>
            </div>
            <div>
              <a-tooltip placement="rightTop">
                <template slot="title">
                  The current cost in credits of active sessions related to the
                  cost incurred when all registered sessions are active.
                </template>
                <strong>Current consumption</strong>
                <a-progress
                  :percent="currentConsumption.percent"
                  :format="() => currentConsumption.current + ' credits/h'"
                  size="small"
                  status="normal"
                  data-test="consumption-credits"
                />
              </a-tooltip>
            </div>
          </section>
          <section v-else class="activeSesstionContainer">
            <a-spin tip="Updating credits..." size="large" class="spinner" />
          </section>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import Button from "@/components/Button.vue";
import SessionDetails from "@/components/SessionDetails.vue";
import {
  deleteSession as deleteSessionRequest,
  stopAllSessions,
  stopSession as stopSessionRequest,
  createVM as createVMRequest,
  connectVNC as connectVNCRequest
} from "@/api/vm";
import { isMachineInActiveState, getMachineState } from "@/utils/vm";

export default {
  name: "ActiveSession",
  components: { Button, SessionDetails },
  props: {
    newWindowLabel: {
      type: String,
      required: true
    },
    isFetchingSession: {
      type: Boolean
    },
    processing: Boolean,
    isVNCOpening: Boolean,
    sessions: {
      type: Array,
      required: true,
      default: () => {
        return [];
      }
    },
    maxConcurrentSessions: [Number],
    hoursForecast: [Number, String],
    totalCreditsForThisMonth: Number,
    usedCreditsThisMonth: Number
  },
  data() {
    return {
      activeKey: [""],
      vmStartingOrStopping: false,
      isRDPDownloading: false
    };
  },
  filters: {
    twoDecimalPlaces: function(value) {
      return this.toTwoDecimalPlaces(value).toFixed(2);
    }
  },
  watch: {
    activeKey(key) {
      const { activeSession } = this.$route.query;
      if (!key) {
        this.$router.replace({ path: this.$route.path });
        return;
      }

      if (key && activeSession !== String(key)) {
        this.$router.replace({
          path: this.$route.path,
          query: { activeSession: key }
        });
      }
    }
  },
  mounted() {
    const { activeSession } = this.$route.query;
    if (activeSession) {
      this.activeKey = [activeSession];
    }
  },
  computed: {
    currentConsumption() {
      var consumption = {
        current: 0,
        max: 0,
        percent: 0
      };
      for (let session of this.sessions) {
        consumption.max += session.template.price;
        consumption.current +=
          session.machineState === "Running" ? session.template.price : 0;
      }
      consumption.percent = this.toTwoDecimalPlaces(
        (100 * consumption.current) / (consumption.max ? consumption.max : 1)
      );

      return consumption;
    },
    remainingCreditsPercentage() {
      return Math.floor(
        (100 / this.totalCreditsForThisMonth) * this.remainingCreditForThisMonth
      );
    },
    remainingCreditsString() {
      return `${Number(this.remainingCreditForThisMonth).toFixed(
        2
      )} / ${Math.round(this.totalCreditsForThisMonth)}`;
    },
    //copied from https://embed.plnkr.co/FFUXhl/preview
    getRemainingDaysInMonth() {
      var date = new Date();
      var time = new Date(date.getTime());

      var toReturn = {
        percentage: 0,
        formatString: ""
      };

      //set first day of next month
      time.setDate(1);
      time.setMonth(date.getMonth() + 1);
      time.setHours(0, 0, 0);
      let diffInDays = parseInt((time - date) / (24 * 3600 * 1000));
      if (this.hoursForecast >= 24 && diffInDays > 0) {
        let forecastDays = Math.round(((this.hoursForecast / 24) * 100) / 100);
        toReturn.percentage = (100 / diffInDays) * forecastDays;
        toReturn.formatString = `${Math.min(forecastDays, diffInDays).toFixed(
          0
        )} / ${diffInDays.toFixed(0)} days`;
        return toReturn;
      }
      let diffInHours = Math.min(24, (time - date) / (3600 * 1000));
      toReturn.percentage = (100 / diffInHours) * this.hoursForecast;

      if (this.hoursForecast && this.hoursForecast > 0) {
        let forecast = Math.min(this.hoursForecast, Math.round(diffInHours));
        toReturn.formatString = `${Number(forecast).toFixed(2)} / ${Number(
          diffInHours
        ).toFixed(0)} hours`;
      } else {
        toReturn.formatString = `not applicable`;
      }
      return toReturn;
    },
    forcastInHours() {
      const val = (
        Math.round(Number(this.hoursForecast || 0) * 100) / 100
      ).toFixed(2);
      if (val > 1) {
        return `${val} hrs`;
      }
      return `${val} hr`;
    },
    activeSessionCount() {
      return this.sessions.filter(session =>
        isMachineInActiveState(session.machineStateValue)
      ).length;
    },
    remainingCreditForThisMonth() {
      const val =
        Number(this.totalCreditsForThisMonth || 0) -
        Number(this.usedCreditsThisMonth || 0);
      return val;
    }
  },
  methods: {
    toTwoDecimalPlaces: function(value) {
      return Math.floor((Number(value || 0) * 100) / 100);
    },
    async handleStopSession(sessionId) {
      try {
        await stopSessionRequest(sessionId);
        this.handleSessionRefresh();
      } catch (error) {
        let message = "Could not stop VM";
        if (error && error.response && error.response.data) {
          message = error.response.data;
        }
        this.$notification.error({
          message: error.message || "Could not stop VM",
          description: message
        });
      }
    },
    async handleRestartSession(startConfiguration) {
      try {
        // check that session exists.
        this.getSession(startConfiguration.sessionId);
        await createVMRequest({
          sessionId: startConfiguration.sessionId,
          timeoutHours: startConfiguration.timeoutHours,
          useVPNSetup: startConfiguration.useVPNSetup
        });
        this.handleSessionRefresh();
      } catch (error) {
        let message = "Could not restart VM";
        if (error && error.response && error.response.data) {
          message = error.response.data;
        }
        this.$notification.error({
          message: "Could not restart session",
          description: message
        });
      }
    },
    // generate an RDP download file for the sessionId and
    // trigger the download of the corresponding file.
    async handleRDPFileDownload(sessionId) {
      try {
        const session = this.getSession(sessionId);
        if (this.isRDPDownloading) {
          return;
        }
        this.isRDPDownloading = true;
        await connectVNCRequest(session.id);
        const textContent = `full address:s:${
          session.useVPNSetup
            ? session.connectionDetails.privateIP
            : session.publicDNS
        }\r\nusername:s:${session.connectionDetails.winAdminUser.trim()}\r\ndomain:s:EDN
        \r\nscreen mode id:i:1
        \r\nsmart sizing:i:1
        \r\ndynamic resolution:i:1
        \r\ndesktopheight:i:1080
        \r\ndesktopwidth:i:1920`;
        const fileName = `${session.label ||
          session.template.model ||
          "eos_machine"}.rdp`;
        this.download(fileName, textContent);
        this.isRDPDownloading = false;

        //copy the password to clipboard
        await navigator.clipboard.writeText(
          session.connectionDetails.winAdminPassword
        );
        // trigger notification
        this.$notification.info({
          message: "Copied windows password to clipboard"
        });
        if (session.useVPNSetup) {
          this.$notification.warn({
            message: "Notice",
            description: "RDP only works with an existing VPN connection!",
            duration: 8
          });
        }
      } catch (error) {
        this.isRDPDownloading = false;
        console.log(error);
        this.$notification.error({
          message: "Could not download RDP file"
        });
      }
    },
    // trigger the download of a file
    // by adding a link and trigger a click.
    download(filename, text) {
      var element = document.createElement("a");
      element.setAttribute(
        "href",
        "data:text/plain;charset=utf-8," + encodeURIComponent(text)
      );
      element.setAttribute("download", filename);

      element.style.display = "none";

      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    },
    getSession(sessionId) {
      const session = this.sessions.find(session => session.id === sessionId);

      if (!session) {
        throw "Invalid session identifier... please refresh and try again.";
      }
      return session;
    },
    isMachineActive(session = {}) {
      return isMachineInActiveState(session.machineStateValue);
    },
    isSessionStateStable(session = {}) {
      var state = this.machineState(session);
      return state === "RUNNING" || state === "STOPPED";
    },
    machineState(session = {}) {
      return getMachineState(session.machineStateValue);
    },
    handleVMStartingOrStopping(value) {
      this.vmStartingOrStopping = value;
    },
    handleSessionRefresh() {
      this.vmStartingOrStopping = false;
      this.$emit("refetchSessions");
    },
    handleClick(event, sessionId) {
      event.stopPropagation();
      const selectedSession = this.sessions.find(s => s.id === sessionId);
      if (!selectedSession) return;
      if (!this.isSessionStateStable(selectedSession)) return;
      this.deleteSession(sessionId);
    },
    async deleteSession(sessionId = "") {
      try {
        await deleteSessionRequest(sessionId);
        this.$emit("refetchSessions");
      } catch (error) {
        this.$notification.error({
          message: error.message || "Could not delete the session",
          description: "Unable to delete session"
        });
      }
    },
    async stopAllVMSessions() {
      try {
        await stopAllSessions(); // stops all session if session ID is not passed
        this.$emit("refetchSessions");
      } catch (error) {
        this.$notification.error({
          message: error.message || "Could not stop all session",
          description: "Unable to stop all sessions"
        });
      }
    },
    truncateString(str = "", num = 30) {
      if (str.length > num) {
        return str.slice(0, num) + "... ";
      } else {
        return str;
      }
    },
    getSessionName(session = {}) {
      if (!session.template) {
        return session.label || "Session";
      }
      const sessionHeaderTitle = `${session.template.model} - ${
        session.template.edition
      } ${session.label ? ":" : ""} ${session.label || ""}`;
      const sessionStatus = getMachineState(session.machineStateValue);
      if (sessionStatus === "STARTING") {
        return `${this.truncateString(
          sessionHeaderTitle
        )}   (Starting Session)`;
      } else if (sessionStatus === "STOPPING") {
        return `${this.truncateString(
          sessionHeaderTitle
        )}   (Stopping Session)`;
      }

      return sessionHeaderTitle;
    },
    handleWindowClick(e, sessionId) {
      this.$emit("newWindowClick", e, sessionId);
    }
  }
};
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
/* global styles */
.ant-collapse-header {
  color: $white !important;
  border: none;
  text-align: left;
}

// Render Bugfix in Collapse (Ant-Design)
// Remove the gray border at the bottom of the accordion
.ant-collapse-borderless > .ant-collapse-item {
  border-bottom: 1px solid white;
}

.ant-collapse-content {
  background-color: $backgroundColor !important;
  border: none;
}
</style>

<style lang="scss" scoped>
@import "../styles/_variables.scss";

.spinner {
  margin-top: 2rem;
}

.main {
  width: 100%;
  background-color: $backgroundColor;
}
.header {
  height: 80px;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding-left: $p4;
  background-color: $white;

  & h3 {
    color: #e60010;
    font-size: 24px;
    font-weight: 400;
    margin: 0;
  }

  & .create-new-session-button {
    margin-left: $m3;
    background: $defaultColor;
    font-size: 1rem;
    font-weight: 500;
    font-family: "Open Sans", sans-serif;
    border-color: $secondaryColor;
    background-color: $secondaryColor;
    height: 2.3125rem;
    padding: 0 0.625rem;
    border-radius: 0.5rem;
    box-shadow: 0 0.5625rem 2.4375rem -0.6875rem rgba(0, 0, 0, 0.75);
    width: 150px;

    color: white;
    & > .add-icon {
      color: white;
      margin-left: $m2;
      font-size: 1.5em;
    }
    & > .add-icon.transparent {
      color: lightgray;
      margin-left: $m2;
      font-size: 1.5em;
    }
  }
}

.content {
  margin: 0;
  background-color: $backgroundColor;
  width: 100%;
  padding-left: $p3;
  padding-right: $p3;
  min-height: 70vh;
  display: flex;
  justify-content: center;
  padding-top: $p4;

  @media screen and (max-width: 769px) {
    flex-direction: column;

    .activeSesstionContainer {
      order: 1;
      width: 100%;
    }
    .creditContainer {
      order: 0;
      width: 100%;
      margin-left: 0;
      margin-bottom: $m2;
    }
  }
}

.creditContainer {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  text-align: left;
  width: 30%;
  height: 430px;
  min-width: 350px;

  & > div {
    background-color: $white;
    padding: $p2;
    margin-left: $m4;
    max-width: 300px;
    & > h3 {
      color: #e60010;
    }
    & > section {
      & div {
        display: flex;
        flex-direction: column;
        margin-top: $m1;
        margin-bottom: $m1;

        & strong {
          font-size: $baseTextSize;
          font-weight: 700;
        }
        & span {
          font-size: $baseTextSize;
        }
        & div {
          max-width: 220px;
        }
      }
    }
  }
}

.activeSesstionContainer {
  width: 60%;
  min-height: 434px;
  background-color: $white;
  margin-bottom: $m2;
  padding: $p2;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  & .opacity {
    opacity: 0.5;
  }

  & .activeSession {
    width: 100%;
    margin-top: $m1;
    margin-bottom: $m1;

    & > .accordionHeader {
      background-color: #556679;
      font-size: $baseTextSize;
      border-radius: 24px;
      border: none;

      &.active {
        background-color: #018cd7;
      }

      & i {
        margin-left: $m1;
      }
    }
  }

  & .actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    & span {
      font-size: $baseTextSize;
      font-weight: 700;
      margin-bottom: $m1;
    }

    & .button {
      height: 40px;
      width: 150px;
      font-size: $baseTextSize;
    }
  }
}
</style>
